import React, {ReactNode, Suspense, useContext, useEffect, useState} from 'react';
import { Route } from 'react-router-dom';
import { RouteConfig } from '../../config/RouteConfig';
import LoadingAnimation from "../common/LoadingAnimation";
import {RouteObject} from "../../models/common/Routes";
import ErrorBoundary from "../common/ErrorBoundary";
import {AuthenticatedUserContext, LanguageContext} from "../../config/ContextConfig";
import LoginForm from "./LoginForm";

/**
 * Generates and manages application routes dynamically, handling authentication, redirection, and language contexts.
 *
 * This function is responsible for rendering application routes based on the provided configuration.
 * It ensures that routes are displayed according to user authentication status and specified route properties.
 * The component dynamically updates whenever the authenticated user's information changes.
 *
 * Technical Details:
 * - Uses `RouteConfig` to map route paths and components.
 * - Handles redirections (`isRedirect`) and route authentication requirements (`authenticationOptional`).
 * - Wraps all routes within an error boundary and a suspense fallback to ensure graceful error handling
 *   and proper loading behavior.
 * - Relies on state (`useState`) to store the dynamically created list of route components.
 * - React contexts (`AuthenticatedUserContext`, `LanguageContext`) are used for authentication and language settings.
 *
 * @return {JSX.Element} A `div` element containing the dynamically generated routes within an `ErrorBoundary`
 *                       and a `Suspense` component.
 *
 * @throws {Error} Routes may fail to render correctly if:
 * - `RouteConfig` is improperly structured or contains missing properties.
 * - Authentication context is misconfigured.
 * - A runtime error occurs while rendering components within the suspense or error boundary wrapper.
 */
function Routes() {

    const [ state, setState ] = useState< ReactNode[] >([]);
    const authUser = useContext( AuthenticatedUserContext );
    const language = useContext( LanguageContext );



    const routes = Object
        .values( RouteConfig )
        .flatMap( ( value: RouteObject ) => Object.values( value.routes ) )

    useEffect(() => {

        let routeComps: React.ReactNode[] = [];

        routes.forEach( _route  => {
            if( _route.isRedirect ) {
                routeComps.push(
                    <Route
                        key={_route.path}
                        path={_route.path}
                        //@ts-ignore
                        exact={_route.exact ? _route.exact : undefined}
                        //@ts-ignore
                        render={_route.component}
                    />
                );
            } else {

                routeComps.push(
                    <Route
                        key={_route.path}
                        path={ _route.path}
                        //@ts-ignore
                        exact={_route.exact ? _route.exact : undefined}
                    >
                        { _route.authenticationOptional || (( authUser?.id?.rawValue ?? 0 ) > 0 )
                            ? <_route.component />
                            : <LoginForm />
                        }
                    </Route>
                );
            }

        });

        setState( routeComps );


    }, [ authUser.id.rawValue ]);


    return (
        <div>
            <ErrorBoundary language={ language }>
                <Suspense fallback={ <LoadingAnimation children={null} /> }>

                    { state }

                </Suspense>
            </ErrorBoundary>

        </div>
     );
}

export default Routes;